import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../lib/auth";
import { api, API } from "../lib/api";
import Navbar from "../components/Navbar";
import Seo from "../components/Seo";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Save, Upload, Image, CheckCircle, Loader2 } from "lucide-react";

const DEFAULT = {
  hero: { badge: "", title: "", description: "", imageUrl: "", imageCaption: "", prosOnlineCount: "", prosOnlineLabel: "" },
  stats: { bookingsCount: "", bookingsLabel: "", ratingValue: "", ratingLabel: "" },
  howItWorks: { badge: "", title: "", steps: [] },
  services: { badge: "", title: "", description: "" },
  cta: { badge: "", title: "", description: "", buttonText: "", image1Url: "", image2Url: "" },
  testimonials: { badge: "", title: "", items: [] },
  registration_fee: 99,
};

const SectionCard = ({ title, desc, children }) => (
  <div className="bg-white rounded-sm border border-[#E2E0D9] shadow-[0_4px_12px_rgba(0,0,0,0.04)] overflow-hidden">
    <div className="px-6 py-5 border-b border-[#E2E0D9] bg-[#FAF9F6]">
      <h3 className="font-display text-lg text-[#1A1D1A]">{title}</h3>
      {desc && <p className="text-sm text-[#646964] mt-0.5">{desc}</p>}
    </div>
    <div className="p-6 space-y-4">{children}</div>
  </div>
);

const Field = ({ label, val, onChange, type = "text", multiline = false }) => (
  <div className="space-y-1.5">
    <label className="text-xs uppercase tracking-wider text-[#646964] font-bold">{label}</label>
    {multiline ? (
      <Textarea value={val} onChange={e => onChange(e.target.value)} className="md:rounded-sm rounded-lg min-h-[80px]" />
    ) : (
      <Input type={type} value={val} onChange={e => onChange(e.target.value)} className="md:rounded-sm rounded-lg h-12 md:h-10" />
    )}
  </div>
);

const ImageField = ({ label, val, uploadingKey, onUpload, onRemove, onChangeUrl }) => (
  <div className="space-y-3">
    <label className="text-xs uppercase tracking-wider text-[#646964] font-bold">{label}</label>
    <div className="relative rounded-sm overflow-hidden border border-[#E2E0D9] bg-[#F3F1ED] aspect-video flex items-center justify-center group">
      {val ? (
        <img src={val} alt="" className="h-full w-full object-contain"
          onError={e => { e.target.style.display = "none"; }}
        />
      ) : (
        <div className="text-center text-[#646964]">
          <Image size={32} className="mx-auto mb-2 opacity-40" />
          <p className="text-xs">No image</p>
        </div>
      )}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center gap-2">
        <Button type="button" onClick={onUpload} disabled={uploadingKey}
          className="opacity-0 group-hover:opacity-100 transition-opacity rounded-lg md:rounded-sm bg-white/90 text-[#1C4532] hover:bg-white shadow-sm px-4 h-10 text-sm font-semibold">
          {uploadingKey ? <Loader2 size={16} className="animate-spin mr-2"/> : <Upload size={16} className="mr-2" />}
          {uploadingKey ? "Uploading..." : "Upload"}
        </Button>
        {val && (
          <button type="button" onClick={onRemove}
            className="opacity-0 group-hover:opacity-100 transition-opacity rounded-lg md:rounded-sm bg-red-500/80 text-white hover:bg-red-500 shadow-sm px-3 h-10 text-xs font-semibold">
            Remove
          </button>
        )}
      </div>
    </div>
    <Input type="url" placeholder="Or paste image URL..." value={val} onChange={e => onChangeUrl(e.target.value)} className="md:rounded-sm rounded-lg h-12 md:h-10 text-sm" />
  </div>
);

export default function AdminCMS() {
  const { user } = useAuth();
  const [content, setContent] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("hero");
  const [uploading, setUploading] = useState(null);
  const fileRef = useRef(null);
  const [pendingTarget, setPendingTarget] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/admin/content").then(r => {
      const merged = {};
      for (const key of Object.keys(DEFAULT)) {
        if (typeof DEFAULT[key] === "object" && DEFAULT[key] !== null) {
          merged[key] = { ...DEFAULT[key], ...(r.data[key] || {}) };
        } else {
          merged[key] = r.data[key] ?? DEFAULT[key];
        }
      }
      setContent(merged);
    }).catch(() => toast.error("Failed to load content"))
    .finally(() => setLoading(false));
  }, [user]);

  const update = (section, key, value) => {
    setContent(prev => ({
      ...prev,
      [section]: typeof key === "string"
        ? { ...prev[section], [key]: value }
        : key,
    }));
  };

  const updateStep = (idx, key, value) => {
    const steps = [...(content.howItWorks.steps || [])];
    steps[idx] = { ...steps[idx], [key]: value };
    update("howItWorks", { ...content.howItWorks, steps });
  };

  const updateTestimonial = (idx, key, value) => {
    const items = [...(content.testimonials.items || [])];
    items[idx] = { ...items[idx], [key]: value };
    update("testimonials", { ...content.testimonials, items });
  };

  const addTestimonial = () => {
    const items = [...(content.testimonials.items || []), { name: "", text: "", rating: 5, avatar: "" }];
    update("testimonials", { ...content.testimonials, items });
  };

  const removeTestimonial = (idx) => {
    const items = (content.testimonials.items || []).filter((_, i) => i !== idx);
    update("testimonials", { ...content.testimonials, items });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", content);
      // Bust landing page cache
      try { localStorage.removeItem("hg_content_cache"); localStorage.removeItem("hg_services_cache"); } catch {}
      toast.success("Content saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const triggerUpload = (section, key) => {
    setPendingTarget({ section, key });
    fileRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    const target = pendingTarget;
    if (!file || !target) return;
    setUploading(target.key);
    try {
      const form = new FormData();
      form.append("file", file);
      const token = localStorage.getItem("hg_token");
      const url = `${API}/admin/upload`;
      const res = await fetch(url, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      let data;
      try { data = await res.json(); } catch { throw new Error("Invalid response"); }
      if (data.ok && data.url) {
        update(target.section, target.key, data.url);
        toast.success("Image uploaded");
      } else {
        toast.error(data.error || "Upload failed");
      }
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(null);
      setPendingTarget(null);
      if (e.target) e.target.value = "";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <Loader2 size={24} className="animate-spin text-[#1C4532]" />
      </div>
    );
  }

  return (
    <><Seo title="Content Management" description="Manage home page content, services, and site settings on Universal Tree." noindex />
    <div className="min-h-screen flex flex-col bg-[#F3F1ED]">
      <Navbar />
      <input type="file" accept="image/*" ref={fileRef} onChange={handleFile} className="hidden" />

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-6 md:py-10 w-full">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#D9734E] font-bold">CMS</p>
            <h1 className="font-display text-3xl lg:text-4xl font-light tracking-tight mt-1 text-[#1A1D1A]">Edit Home Page</h1>
            <p className="text-sm text-[#646964] mt-1">Changes appear on the homepage after saving</p>
          </div>
          <Button onClick={save} disabled={saving} className="rounded-lg md:rounded-sm bg-[#1C4532] hover:bg-[#143324] text-white flex items-center gap-2 px-6 h-12 md:h-10 shadow-[0_4px_12px_rgba(28,69,50,0.2)]">
            {saving ? <Loader2 size={16} className="animate-spin"/> : <Save size={16} />}
            {saving ? "Saving..." : "Save All"}
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <div className="sticky top-0 z-10 bg-[#F3F1ED] pt-2 pb-4 -mx-4 px-4">
            <TabsList className="bg-white md:rounded-sm rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.06)] p-1 h-12 w-full sm:w-auto inline-flex gap-1 border border-[#E2E0D9]">
              <TabsTrigger value="hero" className="md:rounded-sm rounded-lg data-[state=active]:bg-[#1C4532] data-[state=active]:text-white px-5 whitespace-nowrap text-sm font-semibold transition-all">Hero</TabsTrigger>
              <TabsTrigger value="stats" className="md:rounded-sm rounded-lg data-[state=active]:bg-[#1C4532] data-[state=active]:text-white px-5 whitespace-nowrap text-sm font-semibold transition-all">Stats</TabsTrigger>
              <TabsTrigger value="howItWorks" className="md:rounded-sm rounded-lg data-[state=active]:bg-[#1C4532] data-[state=active]:text-white px-5 whitespace-nowrap text-sm font-semibold transition-all">How It Works</TabsTrigger>
              <TabsTrigger value="services" className="md:rounded-sm rounded-lg data-[state=active]:bg-[#1C4532] data-[state=active]:text-white px-5 whitespace-nowrap text-sm font-semibold transition-all">Services</TabsTrigger>
              <TabsTrigger value="cta" className="md:rounded-sm rounded-lg data-[state=active]:bg-[#1C4532] data-[state=active]:text-white px-5 whitespace-nowrap text-sm font-semibold transition-all">CTA</TabsTrigger>
              <TabsTrigger value="testimonials" className="md:rounded-sm rounded-lg data-[state=active]:bg-[#1C4532] data-[state=active]:text-white px-5 whitespace-nowrap text-sm font-semibold transition-all">Testimonials</TabsTrigger>
              <TabsTrigger value="settings" className="md:rounded-sm rounded-lg data-[state=active]:bg-[#1C4532] data-[state=active]:text-white px-5 whitespace-nowrap text-sm font-semibold transition-all">Settings</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="hero" className="mt-2 space-y-6">
            <SectionCard title="Hero Section" desc="Main headline area with background image">
              <Field label="Badge" val={content.hero.badge} onChange={v => update("hero", "badge", v)} />
              <Field label="Title" val={content.hero.title} onChange={v => update("hero", "title", v)} multiline />
              <Field label="Description" val={content.hero.description} onChange={v => update("hero", "description", v)} multiline />
            </SectionCard>
            <SectionCard title="Hero Image" desc="Large background image on the right side">
              <ImageField label="Image URL" val={content.hero.imageUrl} uploadingKey={uploading === "imageUrl"} onUpload={() => triggerUpload("hero", "imageUrl")} onRemove={() => update("hero", "imageUrl", "")} onChangeUrl={v => update("hero", "imageUrl", v)} />
              <Field label="Image Caption" val={content.hero.imageCaption} onChange={v => update("hero", "imageCaption", v)} />
            </SectionCard>
            <SectionCard title="Pros Online Badge" desc="Shows live count overlay on hero image">
              <Field label="Count (e.g. 248)" val={content.hero.prosOnlineCount} onChange={v => update("hero", "prosOnlineCount", v)} />
              <Field label="Label (e.g. pros online near you)" val={content.hero.prosOnlineLabel} onChange={v => update("hero", "prosOnlineLabel", v)} />
            </SectionCard>
          </TabsContent>

          <TabsContent value="stats" className="mt-2 space-y-6">
            <SectionCard title="Stats Tiles" desc="Shown below the hero section">
              <Field label="Bookings Count (e.g. 50K+)" val={content.stats.bookingsCount} onChange={v => update("stats", "bookingsCount", v)} />
              <Field label="Bookings Label" val={content.stats.bookingsLabel} onChange={v => update("stats", "bookingsLabel", v)} />
              <Field label="Rating Value (e.g. 4.9)" val={content.stats.ratingValue} onChange={v => update("stats", "ratingValue", v)} />
              <Field label="Rating Label" val={content.stats.ratingLabel} onChange={v => update("stats", "ratingLabel", v)} />
            </SectionCard>
          </TabsContent>

          <TabsContent value="howItWorks" className="mt-2 space-y-6">
            <SectionCard title="How It Works Header" desc="Section badge and title">
              <Field label="Badge" val={content.howItWorks.badge} onChange={v => update("howItWorks", "badge", v)} />
              <Field label="Title" val={content.howItWorks.title} onChange={v => update("howItWorks", "title", v)} multiline />
            </SectionCard>
            {(content.howItWorks.steps || []).map((step, i) => (
              <SectionCard key={i} title={`Step ${i + 1}`} desc="Icon names: MapPin, Clock, ShieldCheck">
                <Field label="Icon" val={step.icon || ""} onChange={v => updateStep(i, "icon", v)} />
                <Field label="Title" val={step.title} onChange={v => updateStep(i, "title", v)} />
                <Field label="Description" val={step.description} onChange={v => updateStep(i, "description", v)} multiline />
                <Field label="Image URL" val={step.imageUrl || ""} onChange={v => updateStep(i, "imageUrl", v)} />
              </SectionCard>
            ))}
          </TabsContent>

          <TabsContent value="services" className="mt-2 space-y-6">
            <SectionCard title="Services Section" desc="Section header above the service cards">
              <Field label="Badge" val={content.services.badge} onChange={v => update("services", "badge", v)} />
              <Field label="Title" val={content.services.title} onChange={v => update("services", "title", v)} />
              <Field label="Description" val={content.services.description} onChange={v => update("services", "description", v)} multiline />
            </SectionCard>
          </TabsContent>

          <TabsContent value="cta" className="mt-2 space-y-6">
            <SectionCard title="CTA Section" desc="Bottom call-to-action for professionals">
              <Field label="Badge" val={content.cta.badge} onChange={v => update("cta", "badge", v)} />
              <Field label="Title" val={content.cta.title} onChange={v => update("cta", "title", v)} />
              <Field label="Description" val={content.cta.description} onChange={v => update("cta", "description", v)} multiline />
              <Field label="Button Text" val={content.cta.buttonText} onChange={v => update("cta", "buttonText", v)} />
            </SectionCard>
            <SectionCard title="CTA Images" desc="Two images on the right side of the CTA">
              <ImageField label="Image 1 URL" val={content.cta.image1Url} uploadingKey={uploading === "image1Url"} onUpload={() => triggerUpload("cta", "image1Url")} onRemove={() => update("cta", "image1Url", "")} onChangeUrl={v => update("cta", "image1Url", v)} />
              <ImageField label="Image 2 URL" val={content.cta.image2Url} uploadingKey={uploading === "image2Url"} onUpload={() => triggerUpload("cta", "image2Url")} onRemove={() => update("cta", "image2Url", "")} onChangeUrl={v => update("cta", "image2Url", v)} />
            </SectionCard>
          </TabsContent>

          <TabsContent value="testimonials" className="mt-2 space-y-6">
            <SectionCard title="Testimonials Section" desc="Section badge and title">
              <Field label="Badge" val={content.testimonials.badge} onChange={v => update("testimonials", "badge", v)} />
              <Field label="Title" val={content.testimonials.title} onChange={v => update("testimonials", "title", v)} multiline />
            </SectionCard>
            <div className="flex justify-end">
              <Button type="button" onClick={addTestimonial} className="rounded-sm bg-[#1C4532] hover:bg-[#143324] text-white text-sm px-4 h-10">+ Add Testimonial</Button>
            </div>
            {(content.testimonials.items || []).map((item, i) => (
              <SectionCard key={i} title={`Testimonial ${i + 1}`}>
                <div className="flex justify-end">
                  <button type="button" onClick={() => removeTestimonial(i)} className="text-xs text-red-500 hover:text-red-700 font-medium">Remove</button>
                </div>
                <Field label="Name" val={item.name} onChange={v => updateTestimonial(i, "name", v)} />
                <Field label="Quote / Text" val={item.text} onChange={v => updateTestimonial(i, "text", v)} multiline />
                <Field label="Rating (1-5)" type="number" val={String(item.rating || 5)} onChange={v => updateTestimonial(i, "rating", parseInt(v) || 5)} />
                <Field label="Avatar URL" val={item.avatar || ""} onChange={v => updateTestimonial(i, "avatar", v)} />
              </SectionCard>
            ))}
          </TabsContent>

          <TabsContent value="settings" className="mt-2 space-y-6">
            <SectionCard title="Registration" desc="Worker signup settings">
              <Field label="Registration Fee (₹)" type="number" val={String(content.registration_fee ?? 99)} onChange={v => setContent(prev => ({ ...prev, registration_fee: parseFloat(v) || 99 }))} />
            </SectionCard>
          </TabsContent>
        </Tabs>
      </main>
    </div>
    </>
  );
}
